import React, { useMemo, useState } from 'react';
import { Search, ArrowUpRight, X } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { categories, ekgPatterns } from '@/data/ekg';

export const PatternSelector = ({ selectedId, onSelect }) => {
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('all');
  const filtered = useMemo(() => ekgPatterns.filter(pattern => {
    const terms = `${pattern.title} ${pattern.short} ${pattern.tag} ${pattern.summary} ${pattern.focus.map(item => item.lead).join(' ')}`.toLocaleLowerCase('de');
    return (category === 'all' || pattern.category === category) && terms.includes(search.trim().toLocaleLowerCase('de'));
  }), [search, category]);

  return (
    <aside className="ekg-selector" aria-label="EKG-Befunde auswählen" data-testid="ekg-pattern-selector">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-base font-semibold">Befunde</h2>
        <span className="text-xs text-muted-foreground" data-testid="ekg-pattern-count">{filtered.length} / {ekgPatterns.length}</span>
      </div>
      <div className="relative mb-3">
        <Search size={16} className="absolute left-3 top-3.5 text-muted-foreground" aria-hidden="true" />
        <Input className="pl-9 pr-9 h-11 bg-white" aria-label="Befund suchen" placeholder="Befund oder Ableitung …" value={search} onChange={event => setSearch(event.target.value)} data-testid="ekg-search-input" />
        {search && <button className="absolute right-1 top-1 p-2 rounded hover:bg-muted" aria-label="Suche löschen" data-testid="ekg-search-clear" onClick={() => setSearch('')}><X size={18} /></button>}
      </div>
      <label className="sr-only" htmlFor="ekg-category">Befundgruppe</label>
      <select id="ekg-category" className="ekg-native-select" data-testid="ekg-category-select" value={category} onChange={event => setCategory(event.target.value)}>
        {categories.map(item => <option key={item.id} value={item.id}>{item.label}</option>)}
      </select>
      <div className="lg:hidden mt-3">
        <label htmlFor="ekg-mobile-pattern" className="sr-only">EKG-Befund</label>
        <select id="ekg-mobile-pattern" className="ekg-native-select" value={filtered.some(item => item.id === selectedId) ? selectedId : ''} data-testid="ekg-mobile-pattern-select" onChange={event => onSelect(event.target.value)} disabled={!filtered.length}>
          <option value="" disabled>{filtered.length ? 'Befund wählen …' : 'Keine Treffer'}</option>
          {filtered.map(item => <option key={item.id} value={item.id}>{item.short}</option>)}
        </select>
      </div>
      {!filtered.length && <p className="text-sm py-5 text-muted-foreground" data-testid="ekg-no-results">Kein passender Befund. Suche oder Befundgruppe ändern.</p>}
      <nav className="hidden lg:block ekg-pattern-list" aria-label="Befundliste">
        {categories.filter(group => group.id !== 'all').map(group => {
          const items = filtered.filter(pattern => pattern.category === group.id);
          return items.length > 0 && <section key={group.id} className="mt-6">
            <h3 className="text-xs font-semibold text-muted-foreground mb-2">{group.label}</h3>
            {items.map(pattern => <button key={pattern.id} type="button" onClick={() => onSelect(pattern.id)} aria-pressed={selectedId === pattern.id} data-testid={`ekg-pattern-${pattern.id}`} className={`ekg-pattern-button ${selectedId === pattern.id ? 'is-selected' : ''}`}>
              <span>{pattern.short}</span>
              {selectedId === pattern.id && <ArrowUpRight size={16} className="shrink-0" aria-hidden="true" />}
            </button>)}
          </section>;
        })}
      </nav>
    </aside>
  );
};